/**
 * Local UI preferences — sound on/off and friends.
 *
 * These are intentionally local-first. The matching server-side
 * `notification_prefs` (for which kinds get forwarded into the
 * Telegram chat) lives behind /v1/me/notification-prefs and will be
 * wired in Phase 7 when the notifications system lands. Until then
 * we keep the UI-only switches here so the dashboard wow-overlay can
 * already be muted on day one.
 */

import { useEffect, useSyncExternalStore } from 'react';

const KEYS = {
  sound: 'polybot.prefs.sound',
  coveDensity: 'polybot.prefs.cove_density',
} as const;

export type CoveDensity = 'calm' | 'normal' | 'busy';
const DENSITIES: readonly CoveDensity[] = ['calm', 'normal', 'busy'];
const DEFAULT_DENSITY: CoveDensity = 'normal';

const listeners = new Set<() => void>();

function read(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function readString(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* private mode — non-fatal */
  }
  listeners.forEach((l) => l());
}

function writeString(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode — non-fatal */
  }
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  // Cross-tab sync — another tab toggling the pref should update this one.
  const onStorage = (e: StorageEvent) => {
    if (e.key && Object.values(KEYS).includes(e.key as never)) cb();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    window.removeEventListener('storage', onStorage);
  };
}

export function getSoundEnabled(): boolean {
  return read(KEYS.sound);
}

export function setSoundEnabled(v: boolean): void {
  write(KEYS.sound, v);
}

export function useSoundEnabled(): [boolean, (v: boolean) => void] {
  const v = useSyncExternalStore(subscribe, getSoundEnabled, () => false);
  return [v, setSoundEnabled];
}

export function getCoveDensity(): CoveDensity {
  const raw = readString(KEYS.coveDensity);
  return DENSITIES.includes(raw as CoveDensity)
    ? (raw as CoveDensity)
    : DEFAULT_DENSITY;
}

export function setCoveDensity(v: CoveDensity): void {
  writeString(KEYS.coveDensity, v);
}

export function useCoveDensity(): [CoveDensity, (v: CoveDensity) => void] {
  const v = useSyncExternalStore(
    subscribe,
    getCoveDensity,
    () => DEFAULT_DENSITY,
  );
  return [v, setCoveDensity];
}

/** Listen for a preference change without re-rendering. */
export function useOnPrefChange(cb: () => void): void {
  useEffect(() => subscribe(cb), [cb]);
}
